import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Hammer, Ruler, Axe, Wrench, Pencil, Clipboard, HardHat, Box } from 'lucide-react';

const CarpentryWood: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "The Woodshop";
    
    return (
        <div className="min-h-screen bg-[#f3e5d0] font-sans text-[#3e2723] selection:bg-[#8d6e63] selection:text-white border-[20px] border-[#5d4037] relative">
            
            {/* Wood Grain Texture Overlay (CSS Pattern) */}
            <div className="fixed inset-0 opacity-10 pointer-events-none z-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.5' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.5'/%3E%3C/svg%3E")`
            }}></div>

            {/* Navbar - Workbench Style */}
            <nav className="relative z-10 px-8 py-6 bg-[#5d4037] text-[#efebe9] shadow-md flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <div className="bg-[#efebe9] text-[#5d4037] p-2 rounded transform -rotate-3 shadow-inner">
                        <Hammer size={24} />
                    </div>
                    <span className="font-black text-2xl uppercase tracking-tighter">{schoolName}</span>
                </div>
                
                <div className="hidden md:flex gap-8 font-bold uppercase tracking-widest text-xs text-[#d7ccc8]">
                    <a href="#" className="hover:text-white transition-colors">Projects</a>
                    <a href="#" className="hover:text-white transition-colors">Tools</a>
                    <a href="#" className="hover:text-white transition-colors">Safety</a>
                    <a href="#" className="hover:text-white transition-colors">About</a>
                </div>

                <Button className="bg-[#ffab91] hover:bg-[#ff8a65] text-[#3e2723] font-black uppercase tracking-widest text-xs px-6 h-10 border-b-4 border-[#d84315] active:border-b-0 active:translate-y-1 transition-all">
                    Register
                </Button>
            </nav>

            {/* Hero Section */}
            <header className="relative z-10 py-24 px-8 text-center max-w-4xl mx-auto">
                <div className="inline-block border-2 border-[#5d4037] px-6 py-2 rounded-full mb-8 bg-[#efebe9] shadow-[4px_4px_0_#5d4037]">
                    <span className="font-bold uppercase tracking-widest text-xs flex items-center gap-2">
                        <HardHat size={16} /> Est. 1954 • Quality Craftsmanship
                    </span>
                </div>
                
                <h1 className="text-6xl md:text-8xl font-black mb-8 leading-none uppercase text-[#3e2723] drop-shadow-sm">
                    Measure Twice, <br/>
                    <span className="text-[#d84315]">Cut Once.</span>
                </h1>
                
                <p className="text-xl font-medium text-[#5d4037] max-w-2xl mx-auto mb-12 leading-relaxed">
                    Master the timeless art of woodworking. From joinery to cabinetry, learn the skills to build furniture that lasts generations.
                </p>
                
                <div className="flex justify-center gap-4">
                     <Button className="h-16 px-10 text-lg rounded-md bg-[#5d4037] text-white hover:bg-[#4e342e] font-bold uppercase tracking-wider box-border border-2 border-transparent shadow-xl">
                         Start Building
                     </Button>
                     <Button variant="outline" className="h-16 px-10 text-lg rounded-md border-2 border-[#5d4037] text-[#5d4037] hover:bg-[#5d4037] hover:text-white font-bold uppercase tracking-wider bg-transparent">
                         View Blueprints
                     </Button>
                </div>
            </header>

            {/* Pegboard Effect Grid */}
            <section className="relative z-10 py-20 px-8 bg-[#8d6e63] border-y-8 border-[#5d4037]">
                <div className="max-w-6xl mx-auto">
                    <div className="flex justify-between items-center mb-12 text-[#efebe9]">
                        <h2 className="text-4xl font-black uppercase">Core Skills</h2>
                        <Ruler className="opacity-50" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        {[
                            { icon: Axe, title: "Lumber Prep", desc: "Milling & Dimensional Sizing" },
                            { icon: Box, title: "Joinery", desc: "Dovetails & Mortise/Tenon" },
                            { icon: Pencil, title: "Design", desc: "Drafting & Cut Lists" },
                            { icon: Wrench, title: "Finishing", desc: "Staining & Sealing" },
                        ].map((item, i) => (
                            <div key={i} className="bg-[#efebe9] p-8 rounded-sm shadow-[8px_8px_0_rgba(0,0,0,0.2)] hover:transform hover:-translate-y-1 hover:shadow-[12px_12px_0_rgba(0,0,0,0.2)] transition-all cursor-pointer border-2 border-[#5d4037]">
                                <div className="w-16 h-16 bg-[#5d4037] text-[#ffab91] flex items-center justify-center rounded-full mb-6 mx-auto">
                                    <item.icon size={32} />
                                </div>
                                <h3 className="text-xl font-black text-center text-[#3e2723] uppercase mb-2">{item.title}</h3>
                                <p className="text-center text-[#5d4037] font-medium text-xs leading-snug">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="relative z-10 bg-[#3e2723] text-[#d7ccc8] py-20 px-8 text-center">
                 <div className="max-w-2xl mx-auto flex flex-col items-center gap-8">
                     <div className="border-2 border-[#d7ccc8] p-4 inline-block">
                         <span className="font-black text-2xl uppercase tracking-widest">{schoolName}</span>
                     </div>
                     <p className="font-medium text-sm">
                         Safety First. Always wear eye protection. <br/>
                         123 Industrial Park Dr, Workshop B.
                     </p>
                     <div className="flex gap-4">
                         <Clipboard size={20} />
                         <span className="font-bold uppercase tracking-wider text-xs">Certified Instructors</span>
                     </div>
                 </div>
            </footer>
        </div>
    );
};

export default CarpentryWood;
